import React, { useState } from 'react';
import { MousePointer2, Eraser, Layers, Map as MapIcon, Database, Box, Castle, Skull, Package, DoorOpen, Pencil } from 'lucide-react';
import CityPlacer from './CityPlacer';
import EnemyPlacer from './EnemyPlacer';
import ObjectPlacer from './ObjectPlacer';
import PortalEditor from './PortalEditor';
import { resolveTerrainCatalog, normalizeTerrainRenderMode, resolveTilesetImageSrc } from '../../data/tilesets';

const EDITOR_MODES = [
  { id: 'terrain', name: 'Terreno', icon: MapIcon },
  { id: 'cities', name: 'Ciudades', icon: Castle },
  { id: 'enemies', name: 'Enemigos', icon: Skull },
  { id: 'objects', name: 'Objetos', icon: Package },
  { id: 'events', name: 'Eventos', icon: Database },
  { id: 'portals', name: 'Portales', icon: DoorOpen },
];

const TileSelector = ({
  // Modo y herramientas
  activeLayer,
  setActiveLayer,
  selectedTile,
  setSelectedTile,
  editorMode,
  setEditorMode,

  // Datos de ciudades
  cities = [],
  selectedCityId,
  setSelectedCityId,

  // Datos de enemigos
  enemies = [],
  selectedEnemyId,
  setSelectedEnemyId,
  enemyInstances = [],

  // Datos de objetos
  items = [],
  selectedItemId,
  setSelectedItemId,
  isBuried,
  setIsBuried,
  objectInstances = [],

  // Datos de eventos
  missionEvents = [],
  isLoadingMissionEvents = false,
  selectedMissionEventId,
  setSelectedMissionEventId,
  eventInstances = [],
  placedMissionEventMap = new Map(),
  hoveredEventId = null,

  // Datos de portales
  sectors = [],
  currentSectorId,
  portals = [],
  selectedPortalTargetId,
  setSelectedPortalTargetId,
  destinationCoordinate,
  setDestinationCoordinate,
  onPlacePortal,
  onDeletePortal,
  tilesetId = 'overworld_v1',
  tilesetConfig = null,
}) => {
  const terrains = resolveTerrainCatalog(tilesetId, tilesetConfig);
  const [expandedTerrainId, setExpandedTerrainId] = useState(null);
  const apiBaseUrl = import.meta.env.VITE_API_URL || '';
  return (
    <div className="w-80 flex flex-col bg-[#11132d] rounded-3xl border border-slate-800 shadow-xl overflow-hidden h-full">
      {/* Header fijo: Modo de edicion */}
      <div className="p-4 border-b border-slate-800 bg-slate-900/30">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2">
          <Pencil size={14} /> Modo de Edicion
        </h3>
        <div className="flex flex-wrap gap-1">
          {EDITOR_MODES.map((mode) => {
            const Icon = mode.icon;
            return (
              <button
                key={mode.id}
                onClick={() => setEditorMode(mode.id)}
                className={`flex-1 p-2 rounded-lg border flex flex-col items-center gap-1 transition-all min-w-[60px] ${editorMode === mode.id
                    ? 'bg-game-primary/10 border-game-primary/50 text-game-primary'
                    : 'bg-slate-900/50 border-slate-800 text-slate-400 hover:border-slate-700'
                  }`}
                title={mode.name}
              >
                <Icon size={16} />
                <span className="text-[8px] font-bold uppercase">{mode.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Contenido scrollable */}
      <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-6">
        {/* ===================== MODO TERRENO ===================== */}
        {editorMode === 'terrain' && (
          <>
            {/* CAPAS */}
            <section className="space-y-3">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                <Layers size={14} /> Capas
              </h3>
              <div className="flex flex-col gap-2">
                {['Terreno', 'Objetos', 'Entidades'].map((name, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveLayer(i)}
                    className={`flex items-center justify-between px-4 py-3 rounded-xl transition-all border ${activeLayer === i
                        ? 'bg-game-primary/10 border-game-primary/50 text-game-primary'
                        : 'bg-slate-900/50 border-slate-800 text-slate-400 hover:border-slate-700'
                      }`}
                  >
                    <span className="text-sm font-medium">{name}</span>
                    <span className="text-xs opacity-50">L{i}</span>
                  </button>
                ))}
              </div>
            </section>

            {/* HERRAMIENTAS */}
            <section className="space-y-3">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                <MousePointer2 size={14} /> Herramientas
              </h3>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setSelectedTile(null)}
                  className={`p-3 rounded-xl border flex flex-col items-center gap-1 transition-all ${selectedTile === null
                      ? 'bg-rose-500/10 border-rose-500/50 text-rose-500'
                      : 'bg-slate-900/50 border-slate-800 text-slate-400 hover:border-slate-700'
                    }`}
                >
                  <Eraser size={20} />
                  <span className="text-[10px] uppercase font-bold">Borrador</span>
                </button>
              </div>
            </section>

            {/* TERRENOS */}
            <section className="space-y-3">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                <MapIcon size={14} /> Terrenos
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {terrains.map((t) => {
                  const renderMode = normalizeTerrainRenderMode(t);
                  const badge = renderMode === 'autotile' ? 'AT' : renderMode === 'hybrid' ? 'HB' : 'FX';
                  const cells = Array.isArray(t.cells) ? t.cells : [];
                  const hasVariants = cells.length > 1;
                  const isExpanded = expandedTerrainId === t.terrainId;
                  const isTerrainSelected = selectedTile?.type === 'terrain' && selectedTile?.id === t.terrainId;
                  const isAutoSelected = isTerrainSelected && !selectedTile?.variantCell;
                  return (
                    <div key={t.terrainId} className="space-y-2">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedTile({ type: 'terrain', id: t.terrainId });
                          if (hasVariants) {
                            setExpandedTerrainId((current) => (current === t.terrainId ? null : t.terrainId));
                          } else {
                            setExpandedTerrainId(null);
                          }
                        }}
                        className={`w-full p-2 rounded-xl border flex flex-col items-center gap-1 transition-all ${selectedTile?.type === 'terrain' && selectedTile?.id === t.terrainId
                            ? 'bg-game-primary/10 border-game-primary/50 text-white'
                            : 'bg-slate-900/50 border-slate-800 text-slate-400 hover:border-slate-700'
                          }`}
                      >
                        <div className="w-full h-8 rounded-lg shadow-inner bg-gradient-to-br from-slate-500 to-slate-700 flex items-center justify-center">
                          <span className="text-[10px] font-black text-white/90">{badge}</span>
                        </div>
                        <span className="text-[10px] font-bold text-center truncate w-full">{t.name}</span>
                        {hasVariants && (
                          <span className="text-[9px] uppercase tracking-widest text-slate-300/70">{cells.length} variantes</span>
                        )}
                      </button>

                      {hasVariants && isExpanded && (
                        <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-2">
                          <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                            Elige una variante
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              type="button"
                              onClick={() => setSelectedTile({ type: 'terrain', id: t.terrainId })}
                              className={`rounded-lg border px-2 py-3 text-[10px] font-bold uppercase tracking-widest transition-all ${isAutoSelected
                                  ? 'border-game-primary bg-game-primary/10 text-game-primary ring-1 ring-game-primary/40'
                                  : 'border-slate-700 bg-slate-900/60 text-slate-300 hover:border-slate-500'
                                }`}
                              title={`${t.name} en modo automatico`}
                            >
                              Auto
                            </button>
                            {cells.map((cell, index) => {
                              const isActive =
                                isTerrainSelected &&
                                Number(selectedTile?.variantIndex) === index;
                              return (
                                <button
                                  key={`${t.terrainId}-${cell.x}-${cell.y}-${index}`}
                                  type="button"
                                  onClick={() => setSelectedTile({
                                    type: 'terrain',
                                    id: t.terrainId,
                                    variantMode: 'manual',
                                    variantIndex: index,
                                    variantCell: { x: Number(cell.x || 0), y: Number(cell.y || 0) }
                                  })}
                                  className={`aspect-square rounded-lg border overflow-hidden flex items-center justify-center transition-all ${isActive
                                      ? 'border-game-primary bg-game-primary/10 ring-1 ring-game-primary/40'
                                      : 'border-slate-700 bg-slate-900/60 hover:border-slate-500'
                                    }`}
                                  title={`${t.name} variante ${index + 1}`}
                                >
                                  <div
                                    className="w-full h-full bg-cover bg-center"
                                    style={{
                                      backgroundImage: `url(${resolveTilesetImageSrc(tilesetConfig?.image, apiBaseUrl)})`,
                                      backgroundPosition: `-${Number(cell.x || 0) * (tilesetConfig?.tileSize || 48)}px -${Number(cell.y || 0) * (tilesetConfig?.tileSize || 48)}px`,
                                      backgroundSize: `${(tilesetConfig?.tileSize || 48) * (tilesetConfig?.columns || 16)}px auto`
                                    }}
                                  />
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        )}

        {/* ===================== MODO CIUDADES ===================== */}
        {editorMode === 'cities' && (
          <CityPlacer
            cities={cities}
            selectedCityId={selectedCityId}
            setSelectedCityId={setSelectedCityId}
          />
        )}

        {/* ===================== MODO ENEMIGOS ===================== */}
        {editorMode === 'enemies' && (
          <EnemyPlacer
            enemies={enemies}
            selectedEnemyId={selectedEnemyId}
            setSelectedEnemyId={setSelectedEnemyId}
            enemyInstances={enemyInstances}
          />
        )}

        {/* ===================== MODO OBJETOS ===================== */}
        {editorMode === 'objects' && (
          <ObjectPlacer
            items={items}
            selectedItemId={selectedItemId}
            setSelectedItemId={setSelectedItemId}
            isBuried={isBuried}
            setIsBuried={setIsBuried}
            objectInstances={objectInstances}
          />
        )}

        {/* ===================== MODO EVENTOS ===================== */}
        {editorMode === 'events' && (
          <section className="space-y-4">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
              <Database size={14} /> Eventos de Mision
            </h3>
            <select
              value={selectedMissionEventId || ''}
              onChange={(e) => setSelectedMissionEventId(Number(e.target.value) || null)}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white text-sm focus:border-game-primary focus:outline-none"
            >
              <option value="">-- Seleccionar evento --</option>
              {missionEvents.map(ev => {
                const placedInstance = placedMissionEventMap.get(Number(ev.id));
                const shortText = ev.event_text?.slice(0, 40) || 'Evento';
                const suffix = placedInstance
                  ? ` (ya colocado${placedInstance.id_sector ? ` en sector ${placedInstance.id_sector}` : ''}${placedInstance.coordinate !== undefined ? ` coord ${placedInstance.coordinate}` : ''})`
                  : '';
                return (
                  <option
                    key={ev.id}
                    value={ev.id}
                    disabled={!!placedInstance}
                  >
                    #{ev.id} {shortText}{suffix}
                  </option>
                );
              })}
            </select>
            <div className="text-xs text-slate-500">
              Eventos colocados: {eventInstances.length}
            </div>
            {hoveredEventId && (
              <div className="text-[11px] rounded-lg border border-cyan-500/40 bg-cyan-500/10 text-cyan-200 p-3">
                Evento bajo cursor: #{hoveredEventId}
              </div>
            )}
            {isLoadingMissionEvents && (
              <div className="text-[11px] rounded-lg border border-slate-700 bg-slate-900/50 text-slate-300 p-3">
                Cargando eventos de mision...
              </div>
            )}
            {!isLoadingMissionEvents && missionEvents.length === 0 && (
              <div className="text-[11px] rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-200 p-3">
                No hay eventos disponibles. Crea primero una mision y al menos un evento en el panel "Misiones/Eventos".
              </div>
            )}
            <p className="text-[10px] text-cyan-400">
              Selecciona un evento y haz clic en el mapa para colocarlo en capa de entidades.
            </p>

            {eventInstances.length > 0 && (
              <div className="space-y-2">
                <div className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Eventos en este sector</div>
                <div className="max-h-44 overflow-y-auto custom-scrollbar space-y-1">
                  {eventInstances
                    .slice()
                    .sort((a, b) => Number(a.coordinate) - Number(b.coordinate))
                    .map(ev => (
                      <button
                        key={`${ev.coordinate}-${ev.id_event}`}
                        type="button"
                        onClick={() => setSelectedMissionEventId(Number(ev.id_event))}
                        className={`w-full text-left p-2 rounded-lg border transition-colors ${
                          Number(hoveredEventId) === Number(ev.id_event)
                            ? 'border-cyan-400 bg-cyan-500/10'
                            : 'border-slate-700 bg-slate-900/40 hover:border-slate-600'
                        }`}
                      >
                        <div className="text-xs text-white">Coord {ev.coordinate} - Evento #{ev.id_event}</div>
                        <div className="text-[11px] text-slate-400">
                          {ev.mission_name ? `Mision: ${ev.mission_name}` : 'Sin mision'}
                        </div>
                      </button>
                    ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* ===================== MODO PORTALES ===================== */}
        {editorMode === 'portals' && (
          <PortalEditor
            sectors={sectors}
            currentSectorId={currentSectorId}
            portals={portals}
            selectedPortalTargetId={selectedPortalTargetId}
            setSelectedPortalTargetId={setSelectedPortalTargetId}
            destinationCoordinate={destinationCoordinate}
            setDestinationCoordinate={setDestinationCoordinate}
            onPlacePortal={onPlacePortal}
            onDeletePortal={onDeletePortal}
          />
        )}
      </div>
    </div>
  );
};

export default TileSelector;

